import {
  Component,
  ElementRef,
  Inject,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {ApiService} from '../services/api';
import {ActivatedRoute, Router} from '@angular/router';
import {UserContext} from '../services/user-context';
import {MatDialog, MatIconRegistry, MatSnackBar} from '@angular/material';
import {DOCUMENT, TitleCasePipe} from '@angular/common';
import {HttpClient, HttpEvent, HttpEventType} from '@angular/common/http';
import {FileViewerDialogComponent} from './components/file-viewer-dialog.component';
import {PrescriptionDialogComponent} from './components/prescription-dialog.component';
import {DomSanitizer} from '@angular/platform-browser';
import {DataStateChangeEvent, RowArgs, SelectionEvent} from '@progress/kendo-angular-grid';
import {ImageViewerDialogComponent} from './components/image-viewer-dialog.component';
import {State} from "@progress/kendo-data-query";
import {SchedulerEvent} from '@progress/kendo-angular-scheduler';
import * as moment from 'moment';
import {Note} from '../models/note.model';
import {AwsSignatureService} from '../services/auth/aws-signature.service';
import {NoteDialogComponent} from '../clients/components/note-dialog.component';
import {ShopifyService} from '../services/shopify/shopify.service';
import {MDLinkService} from '../services/mdlink/mdlink.service';
import {Subscription} from 'rxjs';


@Component({
  selector: 'app-deferred-session',
  encapsulation: ViewEncapsulation.None,
  templateUrl: 'deferred-session.template.html',
  styleUrls: [
    './deferred-session.style.scss'
  ]
})
export class DeferredSessionComponent implements OnInit, OnDestroy {


  sessionId: string;
  session: any;
  consumer: any;
  assets:any[] = [];
  credentials: any;
  client: any;
  notes: any;
  terminating = false;
  diagnostics: any;
  prescriptions: any;
  loadingProgress = 0;
  loadingStage = "";
  request: any;
  schedulerDate: Date = new Date();
  schedulerEvents: SchedulerEvent[];

  public notesState: State = {
    skip: 0,
    take: 5
  };
  public diagnosticsState: State = {
    skip: 0,
    take: 5
  };
  public prescriptionsState: State = {
    skip: 0,
    take: 5
  };
  private selectedNote: RowArgs;

  private observer:any;
  private socketStatusSubscriber:Subscription;

  constructor(@Inject(DOCUMENT) private document: Document,
              private route: ActivatedRoute,
              private router: Router,
              public userContext: UserContext,
              public dialog: MatDialog,
              private mdlinkService: MDLinkService,
              private snackBar: MatSnackBar,
              private aws: AwsSignatureService,
              private api: ApiService,
              private shopService: ShopifyService,
              public http: HttpClient,
              public iconRegistry: MatIconRegistry,
              sanitizer: DomSanitizer) {

    this.shopService.setShopForCountry(this.userContext.account.country || 'us');

    iconRegistry.addSvgIcon(
      'prescription',
        sanitizer.bypassSecurityTrustResourceUrl('assets/prescription.svg'));
  }


  public ngOnInit(): void {
    this.sessionId = this.route.snapshot.paramMap.get('id');
    this.mdlinkService.connect();
    this.loadingStage = "Authenticating";
    this.getCredentials();

    this.socketStatusSubscriber = this.mdlinkService.connectionSubject.subscribe(connected => {

      if( connected ) {
        console.log("Deferred session connected...");

        this.affirm();

        this.observer = this.mdlinkService.mdLinkSocket.subscribe(message => {
          console.log("Deferred Session received -> " + JSON.stringify(message, null, 4));

          // let message = JSON.parse(socketMessage.data);
          if (message["status"] === "end") {
            console.log("Received termination status");
            this.router.navigate(['telehealth']);
          }
          if (message["status"] === "aborted") {
            console.log("Received termination status");
            this.router.navigate(['telehealth']);
          }
          if (message["status"] === "notification") {
            console.log("Received notification status");
            this.snackBar.open(message["message"], null, {
              duration: 2000
            });
          }
        });
        this.getSession();
      }
    });


    this.snackBar.dismiss();
    this.loadingStage = "Loading Session";
  }


  public ngOnDestroy(): void {
    if( this.observer ) {
      this.observer.unsubscribe();
    }
    if( this.socketStatusSubscriber ) {
      this.socketStatusSubscriber.unsubscribe();
    }
  }

  public diagnosticsStateChange(state: DataStateChangeEvent): void {
    this.diagnosticsState = state;
    this.diagnostics = {
      data: (this.client.diagnostics || []).slice(this.diagnosticsState.skip, this.diagnosticsState.skip + this.diagnosticsState.take),
      total: (this.client.diagnostics || []).length
    };
  }

  public notesStateChange(state: DataStateChangeEvent): void {
    this.notesState = state;
    this.notes = {
      data: (this.client.notes || []).slice(this.notesState.skip, this.notesState.skip + this.notesState.take),
      total: (this.client.notes || []).length
    };
  }

  public prescriptionsStateChange(state: DataStateChangeEvent): void {
    this.prescriptionsState = state;
    this.prescriptions = {
      data: (this.client.prescriptions || []).slice(this.prescriptionsState.skip, this.prescriptionsState.skip + this.prescriptionsState.take),
      total: (this.client.prescriptions || []).length
    };
  }

  public affirm(): void {
    this.api.patchTelehealth<any>(`/request`, {action:"affirm", session:this.sessionId}).subscribe(result => {
      console.log("Affirmed session.")
    }, (error) => {
      this.snackBar.open(`Error affirming session; terminating: ${JSON.stringify(error)}`, null, {
        duration: 2000
      });
    });
  }

  public abort(): void {
    console.log("Hang up");
    this.terminating = true;
    this.api.patchTelehealth<any>(`/request`, {action:"abort"}).subscribe(result => {
      this.snackBar.open(`Aborting...`, null, {
        duration: 2000
      });
    }, (error) => {
      this.terminating = false;
      this.snackBar.open(`Error terminating session: ${error}`, null, {
        duration: 2000
      });
    });
  }

  public hangUp(): void {
    console.log("Hang up");
    this.terminating = true;
    this.api.patchTelehealth<any>(`/request`, {action:"end"}).subscribe(result => {
        this.router.navigate(['telehealth']);
      }, (error) => {
        this.terminating = false;
        this.snackBar.open(`Error terminating session: ${error}`, null, {
          duration: 2000
        });
      }
      );
  }

  public getCredentials(): void {
    console.log("Getting session credentials");
    this.api.getTelehealth<[string]>(`/sessions/${this.sessionId}/credentials`, null).subscribe(result => {
      if(result) {
        console.log("Received credentials: " + JSON.stringify(result,null,4));
        this.credentials = result;
        this.aws.credentials = result;

        this.loadingProgress += 25;
        this.loadingStage = "Connecting to EMR";
        this.getClientFromEMR();
      }
    }, (error) => {
      console.log("Error getting credentials: " + JSON.stringify(error));
      this.snackBar.open(`Error getting emr credentials: ${error.message}`, null, {
        duration: 2000
      });
    });
  }

  public getAssets(): void {
    console.log("Getting session assets");
    this.api.getTelehealth<[string]>(`/sessions/${this.sessionId}/assets`, null).subscribe(result => {
      if(result) {
        console.log("Received assets: " + JSON.stringify(result,null,4));
        result.map( signedUrl => {
          let url = new URL(signedUrl);
          let pathComponents = url.pathname.split("/");
          let filename = pathComponents[pathComponents.length - 1];
          let filenameComponents = (filename || "").split(".");
          let extension = filenameComponents.length > 0 ? filenameComponents[filenameComponents.length - 1] : "-";
          let name = filenameComponents.length > 0 ? filenameComponents[0] : "-";
          console.log("Found file: " + filename);
          this.assets.push({link: signedUrl, name: name, type: this.typeOf(extension)})
        });
        this.loadingProgress += 25;
      }
    }, (error) => {
      console.log("Error getting assets: " + JSON.stringify(error));
      this.snackBar.open(`Error accessing assets: ${error.message}`, null, {
        duration: 2000
      });
    });
  }

  public viewFile(asset:any) {
    console.log("Getting file..." + asset.link);

    this.http.get<any>(asset.link).subscribe( result => {
      console.log("Got file.");
      const dialogRef = this.dialog.open(FileViewerDialogComponent, {
        width: '500px',
        data: result
      });
    });
  }

  public prescribe() {
    const dialogRef = this.dialog.open(PrescriptionDialogComponent, {
      width: '500px',
      data: this.session
    });

    dialogRef.afterClosed().subscribe(result => {
      if ( result ) {
        result.providerId = this.session.provider.id;
        result.providerName = this.userContext.user.name;
        result.consumer = {
          dob: this.client.dob,
          subject: this.client.name || `${this.client.firstName || ""} ${this.client.lastName || ""}`.trim(),
          credentials: this.credentials
        };
        console.log("Received prescription: " + JSON.stringify(result, null, 4));
        this.api.postTelehealth<any>(`/sessions/${this.sessionId}/prescribe`, result).subscribe(prescribeResult => {
          console.log(JSON.stringify(prescribeResult));
          this.api.postEMR<any>(`${this.credentials.path}/prescription`, result ).subscribe( result => {
            this.snackBar.open('Prescription sent successfully.', null, {
              duration: 2000
            });
            this.getClientFromEMR();
          }, (error) => {
            this.snackBar.open(`Error adding prescription: ${error}`, null, {
              duration: 2000
            });
          });
        }, (error) => {
          this.snackBar.open(`Error sending prescription: ${error}`, null, {
            duration: 2000
          });
        });
      }
    });
  }

  public getSession(): void {
    this.api.getTelehealth<any>(`/sessions/${this.sessionId}`)
      .subscribe(result => {
        this.session = result;
        console.log("Found session: " + JSON.stringify(this.session, null, 4));
        this.consumer = this.session.consumer;
        this.request = this.consumer.request;
        this.loadingProgress += 25;
        this.loadingStage = "Loading Assets";

        this.getAssets();
      }, (error) => {
        console.log('Found error ' + JSON.stringify(error));
      });
  }

  public getClientFromEMR() {
    this.api.getEMRProgress(this.credentials.path).subscribe((event: HttpEvent<any>) => {
      switch (event.type) {
        case HttpEventType.Sent:
          console.log('Get Client Request sent!');
          break;
        case HttpEventType.ResponseHeader:
          console.log('Get Client Response header received!');
          break;
        case HttpEventType.Response:
          this.client = event.body;
          this.notes = {total:(this.client.notes || []).length, data: (this.client.notes || []).reverse().slice(this.notesState.skip, this.notesState.skip + this.notesState.take)};
          this.diagnostics = {total:(this.client.diagnostics || []).length, data: (this.client.diagnostics || []).reverse().slice(this.diagnosticsState.skip, this.diagnosticsState.skip + this.diagnosticsState.take)};
          this.prescriptions = {total:(this.client.prescriptions || []).length, data: (this.client.prescriptions || []).reverse().slice(this.prescriptionsState.skip, this.prescriptionsState.skip + this.prescriptionsState.take)};
          console.log("Received client: " + JSON.stringify(this.client, null,4));
          this.loadingProgress += 25;

          var clientEvents: SchedulerEvent[] = [];
          for( const note of this.client.notes ) {
            clientEvents.push( <SchedulerEvent> {
              title: "Note",
              start: moment(note.time).toDate(),
              end: moment(note.time).add(5, 'minutes').toDate()
            });
          }
          for( const diag of this.client.diagnostics ) {
            console.log("Pushing diagnostic to events");
            clientEvents.push( <SchedulerEvent> {
              title: `Diagnostic: ${(diag.type || "").toUpperCase()}`,
              description: diag.type,
              start: moment(diag.time).toDate(),
              end: moment(diag.time).add(5, 'minutes').toDate()
            });
          }
          for( const scrip of this.client.prescriptions ) {
            clientEvents.push( <SchedulerEvent> {
              title: "Prescription",
              description: scrip.name,
              start: moment(scrip.time).toDate(),
              end: moment(scrip.time).add(5, 'minutes').toDate()
            });
          }
          this.schedulerEvents = clientEvents;
          //console.log("Added events: " + JSON.stringify(this.schedulerEvents));
      }


    }, (error) => {
      console.log('Error getting client ' + JSON.stringify(error));
    });
    /*
    this.api.getEMR(this.credentials.path).subscribe(clientResult => {
      this.client = clientResult;
      this.notes = {total:(this.client.notes || []).length, data: (this.client.notes || []).reverse()};
      this.diagnostics = {total:(this.client.diagnostics || []).length, data: (this.client.diagnostics || []).reverse()};
      this.prescriptions = {total:(this.client.prescriptions || []).length, data: (this.client.prescriptions || []).reverse()};
      console.log("Received client: " + JSON.stringify(this.client, null,4));
    }, (error) => {
      console.log('Error getting client ' + JSON.stringify(error));
    });*/
  }

  public selectedAsset(event: SelectionEvent) {
    let asset = event.selectedRows[0].dataItem;
    console.log("Selected asset " + JSON.stringify(asset) );
    if( asset.type === "image" ) {
      this.dialog.open(ImageViewerDialogComponent, {
        width: '960px',
        data: asset
      })
    } else if( asset.type === "file" ) {
      this.viewFile(asset);
    }
  }

  public onNoteSelection(event: SelectionEvent) {
    this.selectedNote = event.selectedRows[0];
  }

  public addNote(): void {
    const dialogRef = this.dialog.open(NoteDialogComponent, {
      width: '900px'
    });

    dialogRef.afterClosed().subscribe(note => {

      if ( note ) {
        this.api.postEMR<any>(`${this.credentials.path}/note`, note ).subscribe( result => {
          this.snackBar.open('Note added.', null, {
            duration: 2000
          });
          this.getClientFromEMR();
        }, (error) => {
          this.snackBar.open(`Error adding note: ${error} for ${this.credentials.path}/note`, null, {
            duration: 2000
          });
        });
      }
    });
  }

  public viewNote(): void {
    const dialogRef = this.dialog.open(NoteDialogComponent, {
      width: ['soap','assessment'].includes(this.selectedNote.dataItem.type) ?  '900px' : '600px',
      data: this.selectedNote.dataItem
    });

    dialogRef.afterClosed().subscribe(location => {
      this.selectedNote = null;
    });
  }

  public typeOf(extension:string): string {
    if(["jpg","png","gif","jpeg"].includes(extension)) {
      return "image"
    } else if(["mov","avi","mp4"].includes(extension)) {
      return "video"
    } else if(["obj","stl"].includes(extension)) {
      return "obj"
    } else if(["json"].includes(extension)) {
      return "file"
    }
    return "-"
  }

  private configurationToString(a) {

    let configuration = '';
    for ( const key in a ) {
      if (a.hasOwnProperty(key)) {
        configuration += key + ' = ' + a[key] + '; ';
      }
    }
    return configuration;
  }

}
